<!--
  Chromeless on purpose.

  A merchant reaches this page from inside Shopify, usually logged out, and
  often before they have ever seen our application. The SideNav and role
  toolbar would be navigation into a product they have not signed into yet, so
  this renders as a single card and nothing else -- the same choice
  `SelectRoleComponent` makes for the same reason.

  It must also load TOP-LEVEL, never inside the Shopify iframe: Cognito keeps
  its session in localStorage, and under browser storage partitioning a
  third-party iframe gets a separate bucket. The new tab is deliberate.
-->
<div class="shopifyLinkShell">
  <div class="shopifyLinkCard">

    <div class="shopifyLinkHeader">
      <mat-icon class="headerIcon">storefront</mat-icon>
      <h1 class="shopifyLinkTitle" i18n="@@Connect your Shopify store">Connect your Shopify store</h1>
    </div>

    <!-- ── working ─────────────────────────────────────────────────── -->
    <div class="stateBlock" *ngIf="state === 'INITIAL' || state === 'NEEDS_LOGIN'">
      <mat-spinner diameter="28"></mat-spinner>
      <span i18n="@@Loading">Loading</span>
    </div>

    <div class="stateBlock" *ngIf="state === 'LINKING'">
      <mat-spinner diameter="28"></mat-spinner>
      <span i18n="@@Connecting your store">Connecting your store</span>
    </div>

    <!-- ── connected ───────────────────────────────────────────────── -->
    <div class="stateBlock success" *ngIf="state === 'LINKED'">
      <mat-icon class="stateIcon">check_circle</mat-icon>
      <div class="stateText">
        <!--
          ONE success sentence, deliberately not branched on `created`.

          It used to say "This store was already connected. Its connection has
          been refreshed" when `created: false`. That reading is no longer
          reliable: the server confirmed on 2026-10-23 that a first attempt
          can create the EcommerceConnection and then fail, so the retry that
          actually succeeds reports `created: false` -- and the merchant would
          be told they had connected before when this was their first success.

          `created` is still logged. It is simply not a safe thing to narrate.
        -->
        <div class="stateHeadline" i18n="@@Your store is connected.">Your store is connected.</div>

        <div class="storeLine" *ngIf="shop">
          <span class="storeLabel" i18n="@@Store">Store</span>
          <span class="storeValue">{{ shop }}</span>
        </div>

        <!--
          The one instruction that matters. Reopening the app is what flips the
          embedded shell onto its `linked: true` branch; without this sentence
          a merchant sits on a success page with nothing to do.
        -->
        <div class="stateHint" i18n="@@ShopifyLinkReturnToShopify">Return to your Shopify tab and open the Snowfall Press app again.</div>
      </div>
    </div>

    <!--
      `warnings` and `setupStatus` are NOT rendered here. They are operator
      diagnostics -- they name server environment variables, and one variant
      carries a raw exception message -- so they are logged by the component
      instead. A merchant who has just connected a store successfully must not
      be handed our internals as though something were wrong.
      See inbox/_processed/2026-10-22h-... and the component's success handler.
    -->

    <!-- ── opened without a link ───────────────────────────────────── -->
    <div class="stateBlock problem" *ngIf="state === 'NEEDS_NONCE'">
      <mat-icon class="stateIcon">link_off</mat-icon>
      <div class="stateText">
        <div class="stateHeadline" i18n="@@ShopifyLinkNeedsNonceHeadline">Open this page from the Snowfall Press app in Shopify.</div>
        <div class="stateHint" i18n="@@ShopifyLinkNeedsNonceHint">In your Shopify admin, go to Apps, open Snowfall Press, and choose the option to connect your account. This page cannot be opened directly or reloaded.</div>
      </div>
    </div>

    <!-- ── signed in, wrong role ───────────────────────────────────── -->
    <div class="stateBlock problem" *ngIf="state === 'NEEDS_ORG'">
      <mat-icon class="stateIcon">account_circle</mat-icon>
      <div class="stateText">
        <div class="stateHeadline" i18n="@@ShopifyLinkNeedsOrganizationHeadline">Choose the organization this store belongs to.</div>
        <div class="stateHint" i18n="@@ShopifyLinkNeedsOrganizationHint">You are signed in, but you are not currently working as an organization. Your store is connected to one organization, so pick the right one before continuing.</div>
        <div class="stateActions">
          <button mat-flat-button color="primary" (click)="chooseOrganizationRole()">
            <span i18n="@@Choose organization">Choose organization</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ── expired or already used ─────────────────────────────────── -->
    <div class="stateBlock problem" *ngIf="state === 'EXPIRED'">
      <mat-icon class="stateIcon">hourglass_disabled</mat-icon>
      <div class="stateText">
        <!--
          This covers TWO situations that we cannot currently tell apart, and
          one of them is a success.

          The server's embedded panel now renders a real anchor plus a Copy
          button, so a merchant can reach this page twice with the same nonce
          -- and the second visit, after a successful redeem, finds the nonce
          burnt. Until the ALREADY_REDEEMED / NONCE_EXPIRED codes ship there
          is no way to distinguish "you already did this" from "it timed out".

          So the copy leads with the reassuring branch and states the rule,
          rather than telling someone who has just succeeded that something
          expired and sending them back to start again. New ids rather than
          edited text: the previous wording's meaning has changed materially,
          and reusing an id would leave 21 bundles translating the old sense.
        -->
        <div class="stateHeadline" i18n="@@ShopifyLinkUnusableHeadline">This connection link cannot be used again.</div>
        <div class="stateHint" i18n="@@ShopifyLinkUnusableHint">If you have already connected this store, you are finished and can close this tab. Otherwise, a link works only once and only within ten minutes, so reopen the Snowfall Press app in your Shopify admin to get a new one.</div>
        <div class="serverDetail" *ngIf="serverMessage">
          <span class="serverDetailLabel" i18n="@@Technical detail">Technical detail</span>
          <span class="serverDetailText">{{ serverMessage }}</span>
        </div>
      </div>
    </div>

    <!-- ── 409: already redeemed, or bound elsewhere ───────────────── -->
    <div class="stateBlock problem" *ngIf="state === 'CONFLICT'">
      <mat-icon class="stateIcon">report_problem</mat-icon>
      <div class="stateText">
        <div class="stateHeadline" i18n="@@ShopifyLinkConflictHeadline">This store could not be connected.</div>
        <!--
          Our translated guidance LEADS and covers both causes -- "already
          redeemed" and "bound to a different organization" -- because the
          server's sentence, which is the only thing that currently separates
          them, is English in all 21 locales. It used to be rendered first and
          prominently; that put untranslated text in the primary position on a
          merchant-facing screen.

          It is kept below as a labelled technical detail: it names the actual
          shop and state and is what support will ask for. When the server
          ships the ALREADY_REDEEMED / SHOP_BOUND_TO_OTHER_ORG machine codes
          this screen can branch on the code and show one translated path.
        -->
        <div class="stateHint" i18n="@@ShopifyLinkConflictHint">If the store is already connected, reopen the Snowfall Press app in Shopify to check. If it belongs to a different Snowfall Press organization, contact support - it cannot be moved from here.</div>
        <div class="serverDetail" *ngIf="serverMessage">
          <span class="serverDetailLabel" i18n="@@Technical detail">Technical detail</span>
          <span class="serverDetailText">{{ serverMessage }}</span>
        </div>
      </div>
    </div>

    <!-- ── anything else ───────────────────────────────────────────── -->
    <div class="stateBlock problem" *ngIf="state === 'FAILED'">
      <mat-icon class="stateIcon">error_outline</mat-icon>
      <div class="stateText">
        <div class="stateHeadline" i18n="@@ShopifyLinkFailedHeadline">Your store could not be connected.</div>
        <div class="stateHint" i18n="@@ShopifyLinkFailedHint">Nothing has been changed. You can try again, or reopen the Snowfall Press app in Shopify for a new link.</div>
        <div class="serverDetail" *ngIf="serverMessage">
          <span class="serverDetailLabel" i18n="@@Technical detail">Technical detail</span>
          <span class="serverDetailText">{{ serverMessage }}</span>
        </div>
        <div class="stateActions">
          <button mat-flat-button color="primary" (click)="advance()">
            <mat-icon>refresh</mat-icon>
            <span i18n="@@Retry">Retry</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</div>
